import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { pdApi, type PdPathwayOpportunity } from '../api';
import './DeliveryPathway.css';
import { HelpPanel } from '../HelpPanel';
import { DELIVERY_PATHWAY } from '../helpContent';
import { SelectField, PROMPT } from '@platform/ui-kit';

/**
 * Delivery Pathway.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/delivery-pathway.ejs` and
 * `routes/index.js:5886-5948`. Headings, node labels, guardrail wording and the colour
 * tokens are the source's.
 *
 * THE RULE THE WHOLE SCREEN EXISTS TO ENFORCE: a pathway can be changed until the process
 * it selected has STARTED, and not after.
 *
 *   * Option A has started once a delivery project exists (`roadmap_execution`).
 *   * Option B has started once effort planning exists (`opportunity_effort_planning`).
 *
 * A started opportunity still renders and is still selectable — it shows a lock and is
 * refused at apply time with a reason. That is deliberate in the source and worth keeping:
 * someone needs to see WHY something cannot move, not have it quietly disappear.
 *
 * The same predicate runs here and on the server. This copy drives the lock icon and the
 * confirmation text; the server's is the boundary, and a client that skipped this check
 * would still be refused.
 */

const A_NODES = [
  { kind: 'Selected', label: 'Opportunities' },
  { kind: 'Create', label: 'Delivery project' },
  { kind: 'As-is', label: 'Program Management' },
  { kind: 'End', label: 'Deploy & track' },
];

const B_NODES = [
  { kind: 'Selected', label: 'Opportunities' },
  { kind: 'Required', label: 'Effort & Value' },
  { kind: 'Step', label: 'Roadmap Planning' },
  { kind: 'Converges', label: 'Program Management' },
  { kind: 'End', label: 'Deploy & track' },
];

/** The source's predicate, character for character in intent. */
/** The picker's all-projects sentinel. Not a project id, so it cannot collide with one. */
const ALL_PROJECTS = '*';

function started(o: PdPathwayOpportunity): boolean {
  return (
    (o.delivery_pathway === 'A' && o.has_delivery_project) ||
    (o.delivery_pathway === 'B' && o.has_effort_planning)
  );
}

export function DeliveryPathway() {
  const [projects, setProjects] = useState<
    { project_id: string; project_description: string; status: string; opp_count: number }[]
  >([]);
  const [all, setAll] = useState<PdPathwayOpportunity[]>([]);
  const [project, setProject] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /*
    THE PAGE'S HEIGHT IS MEASURED, NOT TYPED.

    This shell does not bound its content area — `<main>` carries `min-height: 100vh` and
    then GROWS with whatever it holds — so `height: 100%` on this page has nothing to
    resolve against and the window scrolls. The height available is `innerHeight` minus
    this element's own top offset, and that offset depends on the shell's header, the
    breadcrumb and the viewport, none of which this screen can see.

    So it is measured. CLAUDE.md is explicit about this after two rounds were lost to
    sticky offsets typed as constants: "when a layout value depends on something the
    browser already knows, measure it or let the layout compute it".

    A CALLBACK REF, not `useRef` — the node mounts after the loading gate below, and an
    effect keyed on a `useRef` would run once against `null` and never apply a height.
    That exact mistake made the Users screen's lock silently do nothing.
  */
  const [helpSignal, setHelpSignal] = useState(0);
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);

  const [resultA, setResultA] = useState<{ text: string; err?: boolean } | null>(null);
  const [resultB, setResultB] = useState<{ text: string; err?: boolean } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pdApi.deliveryPathway.load();
      setProjects(data.projects);
      setAll(data.opportunities);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * ALL-PROJECTS MODE — spec 14.3. `ALL_PROJECTS` in the picker loads every project's
   * opportunities at once, grouped by project and each group led by its own project row.
   *
   * Not a convenience. Program Quick Start now leaves every opportunity it creates UNROUTED
   * (change record §5.1), so a 90-initiative intake lands here as ~90 separate decisions.
   * Routing them one project at a time is the difference between this screen being usable and
   * not, and the card-level Select all still covers everything on screen.
   */
  const rows = useMemo(
    () =>
      project === ALL_PROJECTS
        ? all
        : project
          ? all.filter((o) => o.project_id === project)
          : [],
    [all, project],
  );

  /*
   * THE SINGLE GATE — spec 14.2, `dpSelectableRows()`. Select all, the counts, project-row
   * syncing and Apply ALL derive from this one function, so nothing can leak into one of them
   * that is excluded from the others.
   *
   * The source filters out rows already in execution (`data-started`). THIS PORT DOES NOT, on
   * the owner's ruling of 2026-08-09: planning and execution are separate modules here and
   * `project_management` never reads `delivery_pathway`, so an opportunity can sit on B for
   * planning while its delivery project runs on in execution. The gate is kept as a single
   * function anyway — it is the place that rule lives, and a future rule that re-locks rows
   * changes one line rather than five call sites.
   */
  const selectableRows = useMemo(() => rows, [rows]);

  const selectedRows = useMemo(
    () => selectableRows.filter((o) => selected.has(o.opportunity_id)),
    [selectableRows, selected],
  );

  /* Rows grouped under their project, in the picker's order, for all-projects mode. */
  const groups = useMemo(() => {
    const byProject = new Map<string, PdPathwayOpportunity[]>();
    for (const o of rows) {
      if (!byProject.has(o.project_id)) byProject.set(o.project_id, []);
      byProject.get(o.project_id)!.push(o);
    }
    return [...byProject.entries()].map(([pid, opps]) => ({
      pid,
      description: projects.find((p) => p.project_id === pid)?.project_description ?? '',
      opps,
    }));
  }, [rows, projects]);

  /*
   * A project row is ticked when EVERY selectable opportunity under it is ticked — spec 14.2's
   * `dpSyncProjectRows`, derived here rather than stored, so the two can never disagree.
   */
  const projectChecked = (pid: string) => {
    const kids = selectableRows.filter((r) => r.project_id === pid);
    return kids.length > 0 && kids.every((r) => selected.has(r.opportunity_id));
  };

  /** Clicking a project row toggles its whole group — spec 14.2's `dpToggleGroup`. */
  const toggleGroup = (pid: string, on: boolean) => {
    const next = new Set(selected);
    for (const r of selectableRows.filter((x) => x.project_id === pid)) {
      if (on) next.add(r.opportunity_id);
      else next.delete(r.opportunity_id);
    }
    setSelected(next);
  };

  /*
   * "Already on Option X" counts only opportunities somebody actually PUT there.
   *
   * Without the `pathway_chosen` test this reads the display default and claims every
   * undecided opportunity is already on B — the same false statement as the switch warning
   * (change record §5.2), in the place a reviewer is most likely to believe it.
   */
  const countOn = (pathway: 'A' | 'B') =>
    selectedRows.filter((o) => o.pathway_chosen && o.delivery_pathway === pathway).length;

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const selectAll = (on: boolean) =>
    setSelected(on ? new Set(selectableRows.map((o) => o.opportunity_id)) : new Set());

  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      // Settle rather than oscillate: shrinking this page shrinks `main`, which fires the
      // observer again. Ignoring sub-2px deltas stops that becoming a loop.
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  const apply = async (pathway: 'A' | 'B') => {
    const setResult = pathway === 'A' ? setResultA : setResultB;
    const swaps: string[] = [];
    const blocked: { id: string; why: string }[] = [];

    /*
      No longer blocked once delivery has started — see the note on the server handler.
      An opportunity may sit on B for planning while its delivery project runs on in
      execution, because the execution module does not read this column at all.
    */
    /*
     * THREE BUCKETS, NOT TWO — change record §5.3.
     *
     * `firstTime` is the one that was missing. An opportunity nobody has routed yet is not
     * being SWITCHED to anything, so warning "this switches N opportunities — their pathway
     * will change" was simply untrue, and it appeared on the most common action there is:
     * routing freshly created work for the first time. Those apply silently.
     *
     * A genuine A↔B swap still warns, because that one really does change a decision somebody
     * already made.
     */
    const firstTime: string[] = [];
    for (const o of selectedRows) {
      if (!o.pathway_chosen) {
        firstTime.push(o.opportunity_id);
        continue;
      }
      if (o.delivery_pathway === pathway) continue; // already explicitly there
      swaps.push(o.opportunity_id);
    }

    if (!swaps.length && !firstTime.length && !blocked.length) {
      setResult({ text: `All selected are already on Option ${pathway}.` });
      return;
    }
    if (blocked.length && !swaps.length) {
      setResult({
        text: `⛔ Cannot switch to Option ${pathway}: ${blocked
          .map((b) => `${b.id} — ${b.why}`)
          .join('; ')}`,
        err: true,
      });
      return;
    }

    /* Only a genuine switch is confirmed. A run of first-time decisions goes through with no
       dialogue at all — there is nothing to warn about. */
    if (swaps.length) {
      const warn =
        `Warning: this switches ${swaps.length} opportunit${swaps.length === 1 ? 'y' : 'ies'} to Option ${pathway} — their pathway will change.` +
        (blocked.length
          ? `\n\n${blocked.length} cannot switch (process already started) and will be skipped.`
          : '') +
        `\n\nContinue?`;
      if (!window.confirm(warn)) return;
    }

    setResult({ text: 'Applying…' });
    try {
      const d = await pdApi.deliveryPathway.apply(pathway, [...firstTime, ...swaps]);
      if (!d.success) {
        setResult({ text: d.error ?? 'Failed.', err: true });
        return;
      }
      const srvBlocked = d.blocked ?? [];
      setResult({
        text:
          `✓ Saved · ${d.changed ?? 0} assigned to Option ${pathway}` +
          (srvBlocked.length ? ` · ⛔ ${srvBlocked.length} blocked (process started)` : ''),
      });
      await load();
    } catch (e) {
      setResult({ text: `Error: ${(e as Error).message}`, err: true });
    }
  };

  return (
    <div className="dp-screen" ref={setRootEl} style={rootH ? { height: rootH } : undefined}>
      <div className="dp-header">
        <div>
          <h1 className="dp-title">Delivery Pathway</h1>
          <p className="dp-sub">
            Choose a project, pull in its opportunities, and route them to delivery — the
            express lane, or the guided value path.
          </p>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={DELIVERY_PATHWAY} openSignal={helpSignal} />
        </div>
      </div>

      {error ? <div className="dp-error">{error}</div> : null}

      <div className="dp-grid">
        <div className="dp-card">
          <div className="dp-card-title">
            Project &amp; opportunities
            {/*
              The guardrails used to sit as a block at the bottom of this page, which cost
              the two panels the height they needed. They are in the drawer now — the
              source's own arrangement — and this is the way in, beside the thing it
              explains (owner, 2026-08-09).
            */}
            <button
              type="button"
              className="dp-info"
              title="How the Delivery Pathway works — including the guardrails"
              aria-label="How the Delivery Pathway works"
              onClick={() => setHelpSignal((n) => n + 1)}
            >
              i
            </button>
          </div>

          <label className="dp-selectall">
            <input
              type="checkbox"
              checked={selectableRows.length > 0 && selected.size === selectableRows.length}
              disabled={selectableRows.length === 0}
              onChange={(e) => selectAll(e.target.checked)}
            />
            Select all
          </label>

          <SelectField
            label="Project"
            width="lg"
            placeholder={PROMPT.project}
            value={project}
            onChange={(v) => {
              setProject(v);
              setSelected(new Set());
              setResultA(null);
              setResultB(null);
            }}
            options={[
              /* Spec 14.3 — one initiative's worth of decisions is rarely the whole job.
                 A real choice, not a prompt, so it keeps its own row. */
              { value: ALL_PROJECTS, label: `★ All projects (${all.length} opportunities)` },
              /* No longer sliced to 48 characters: the field wraps, so the whole
                 description is readable instead of being cut mid-word. */
              ...projects.map((p) => ({
                value: p.project_id,
                id: p.project_id,
                description: `${p.project_description ?? ''}  (${p.opp_count})`.trim(),
              })),
            ]}
          />

          <div className="dp-opps">
            {loading ? (
              <div className="dp-empty">Loading…</div>
            ) : !project ? (
              <div className="dp-note">Select a project to load its opportunities.</div>
            ) : rows.length === 0 ? (
              <div className="dp-empty">
                {project === ALL_PROJECTS
                  ? 'No opportunities on any project.'
                  : 'No opportunities on this project.'}
              </div>
            ) : (
              groups.map((g) => (
                <Fragment key={g.pid}>
                  {/*
                    THE PROJECT ROW — spec 14.2. Rendered only in all-projects mode: with a
                    single project chosen it would duplicate the picker and the Select all
                    above, which already toggle exactly this group.

                    Two-way sync: clicking it toggles the whole group, and its own ticked state
                    is DERIVED from its children rather than stored, so the two cannot drift.
                  */}
                  {project === ALL_PROJECTS ? (
                    <button
                      type="button"
                      className={`dp-box dp-proj${projectChecked(g.pid) ? ' dp-on' : ''}`}
                      onClick={() => toggleGroup(g.pid, !projectChecked(g.pid))}
                      title={`Select every opportunity on ${g.pid}`}
                    >
                      <span className="dp-oid">{g.pid}</span>
                      <span className="dp-odesc">{g.description}</span>
                      <span className="dp-projcount">
                        {g.opps.length}
                      </span>
                    </button>
                  ) : null}

                  {g.opps.map((o) => {
                    const isStarted = started(o);
                    return (
                      <button
                        key={o.opportunity_id}
                        type="button"
                        className={`dp-box${selected.has(o.opportunity_id) ? ' dp-on' : ''}${
                          project === ALL_PROJECTS ? ' dp-child' : ''
                        }`}
                        onClick={() => toggle(o.opportunity_id)}
                      >
                        <span className="dp-oid">{o.opportunity_id}</span>
                        <span className="dp-odesc">{o.short_desc}</span>
                        {/*
                          NO A/B BADGE. It said an opportunity was on one pathway or the other,
                          and since 2026-08-09 it can hold both — so a single letter could only
                          ever be half the truth. Removed on the owner's instruction. The ▶
                          below still marks work already under way, and names which option.

                          THE `?` BADGE IS A DIFFERENT CLAIM, and that is why it is here while
                          A and B are not. It does not say which lane an opportunity is on; it
                          says NOBODY HAS DECIDED — which is unambiguous however many lanes a
                          record can hold, and is exactly what `pathway_chosen` now makes
                          knowable. Design spec Part 10 principle 6: "undecided is a first-class
                          state, not an absence", which is why it is drawn dashed where a solid
                          badge would read as a value.

                          It matters more since change record §5.1: every route into the system
                          — manual entry, Excel upload, Quick Start — now lands here undecided,
                          so this is the normal state of new work rather than an edge case.
                        */}
                        {!o.pathway_chosen ? (
                          <span
                            className="dp-badge dp-undecided"
                            title="No pathway chosen yet — pick Option A or Option B below."
                          >
                            ?
                          </span>
                        ) : null}
                        {isStarted ? (
                          <span
                            className="dp-lock"
                            title={`Option ${o.delivery_pathway} is already under way for this one — it can still be assigned the other pathway as well`}
                          >
                            ▶
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </Fragment>
              ))
            )}
          </div>

          {project ? (
            <div className="dp-selnote">
              <b>{selected.size}</b> of {selectableRows.length} selected
              {selected.size > 0 ? ' · choose a pathway →' : ''}
            </div>
          ) : null}
        </div>

        <div className="dp-options">
          <div className={`dp-opt dp-opt-a${countOn('A') > 0 ? ' dp-assigned' : ''}`}>
            <div className="dp-opt-head">
              <span className="dp-opt-badge">A</span>
              <div>
                <div className="dp-opt-title">Direct to Execution</div>
                <div className="dp-opt-sub">
                  Fast track — no effort or business-benefit analysis
                </div>
              </div>
            </div>

            <div className="dp-tier-label">Optional — you can still run these</div>
            <div className="dp-flow">
              <span className="dp-node dp-node-opt">AI Platform Fit</span>
              <span className="dp-node dp-node-opt">Financial Modeling</span>
            </div>

            <div className="dp-tier-label">— straight to delivery —</div>
            <div className="dp-flow">
              {A_NODES.map((n) => (
                <span key={n.label} className="dp-node">
                  <span className="dp-node-kind">{n.kind}</span>
                  {n.label}
                </span>
              ))}
            </div>

            <button
              type="button"
              className="dp-apply dp-apply-a"
              disabled={selected.size === 0}
              onClick={() => apply('A')}
            >
              Apply Option A to selection →
            </button>
            <div className="dp-hint">editable until a delivery project is created</div>
            {countOn('A') > 0 ? (
              <div className="dp-assigned-note">
                {countOn('A') === selectedRows.length
                  ? '✓ Already assigned to Option A'
                  : `✓ ${countOn('A')} of ${selectedRows.length} already on Option A`}
              </div>
            ) : null}
            {resultA ? (
              <div className={`dp-result${resultA.err ? ' dp-result-err' : ''}`}>
                {resultA.text}
              </div>
            ) : null}
          </div>

          <div className={`dp-opt dp-opt-b${countOn('B') > 0 ? ' dp-assigned' : ''}`}>
            <div className="dp-opt-head">
              <span className="dp-opt-badge">B</span>
              <div>
                <div className="dp-opt-title">Full Planning Path</div>
                <div className="dp-opt-sub">
                  Effort &amp; business benefits analyzed and defined
                </div>
              </div>
            </div>

            <div className="dp-tier-label">
              Optional — run these, or reuse work already provided
            </div>
            <div className="dp-flow">
              <span className="dp-node dp-node-opt">Score</span>
              <span className="dp-node dp-node-opt">Platform Fit</span>
              <span className="dp-node dp-node-opt">Financial</span>
              <span className="dp-node dp-node-opt">Optimizer · B only</span>
            </div>

            <div className="dp-tier-label">— core planning (required) —</div>
            <div className="dp-flow">
              {B_NODES.map((n) => (
                <span key={n.label} className="dp-node">
                  <span className="dp-node-kind">{n.kind}</span>
                  {n.label}
                </span>
              ))}
            </div>

            <button
              type="button"
              className="dp-apply dp-apply-b"
              disabled={selected.size === 0}
              onClick={() => apply('B')}
            >
              Apply Option B to selection →
            </button>
            <div className="dp-hint">optional stages stay togglable until locked</div>
            {countOn('B') > 0 ? (
              <div className="dp-assigned-note">
                {countOn('B') === selectedRows.length
                  ? '✓ Already assigned to Option B'
                  : `✓ ${countOn('B')} of ${selectedRows.length} already on Option B`}
              </div>
            ) : null}
            {resultB ? (
              <div className={`dp-result${resultB.err ? ' dp-result-err' : ''}`}>
                {resultB.text}
              </div>
            ) : null}
          </div>
        </div>
      </div>

    </div>
  );
}
